import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { crypto as cv } from '@photo-vault/shared';
import { Button, Card, Icon, Input, PageEyebrow } from '../../ui/index.js';
import { useAuthSession } from '../../auth/AuthContext.js';
import { useInfra } from '../../infra/context.js';
import { getAlbumKey } from '../albums/albumKeys.js';
import { decryptTagsCached, forgetRoot, type DecryptedTag } from './tagCache.js';

interface Loaded {
  albumId: string;
  rootAlbumId: string;
  albumKey: CryptoKey;
  tags: DecryptedTag[];
}

// Owner-only list page. Reached from AlbumDetailPage's "Tag" card.
// Resolves K_album from the root and lists every TagDefinition under that
// root, with inline rename/delete affordances and a create-new form.
export function TagsListPage() {
  const { albumId } = useParams<{ albumId: string }>();
  const session = useAuthSession();
  const { metadata } = useInfra();
  const [state, setState] = useState<Loaded | 'loading' | { error: string }>('loading');
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  async function reload() {
    if (!albumId) return;
    setState('loading');
    try {
      const { albums } = await metadata.listAlbums(session.jwt);
      const self = albums.find((a) => a.id === albumId);
      if (!self) {
        setState({ error: 'album non trovato' });
        return;
      }
      const root = albums.find((a) => a.id === self.rootAlbumId);
      if (!root || !root.wrappedAlbumKey) {
        setState({ error: 'chiave radice mancante' });
        return;
      }
      const albumKey = await getAlbumKey(root.rootAlbumId, root.wrappedAlbumKey, session.rsaPriv);
      const { tags } = await metadata.listTags(root.rootAlbumId, session.jwt);
      const decrypted = await decryptTagsCached(tags, root.rootAlbumId, albumKey);
      setState({ albumId, rootAlbumId: root.rootAlbumId, albumKey, tags: decrypted });
    } catch (err) {
      setState({ error: err instanceof Error ? err.message : 'errore' });
    }
  }

  useEffect(() => {
    void reload();
  }, [albumId, session.jwt]);

  const sortedTags = useMemo(() => {
    if (typeof state !== 'object' || 'error' in state) return [] as DecryptedTag[];
    const collator = new Intl.Collator(undefined, { sensitivity: 'base' });
    return [...state.tags].sort((a, b) => collator.compare(a.name, b.name));
  }, [state]);

  if (!albumId) return null;
  if (state === 'loading') return <p className="py-6 text-sm text-on-surface-variant">Carico…</p>;
  if ('error' in state) return <p className="py-6 text-sm text-error">Errore: {state.error}</p>;

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    setCreateError(null);
    const name = newName.trim();
    if (!name) {
      setCreateError('inserisci un nome');
      return;
    }
    if (typeof state !== 'object' || 'error' in state) return;
    const collator = new Intl.Collator(undefined, { sensitivity: 'base' });
    if (state.tags.some((t) => collator.compare(t.name, name) === 0)) {
      setCreateError('esiste già un tag con questo nome');
      return;
    }
    setCreating(true);
    try {
      const tagId = crypto.randomUUID();
      const encryptedName = await cv.encryptText(name, state.albumKey);
      await metadata.createTag(
        { rootAlbumId: state.rootAlbumId, tagId, encryptedName },
        session.jwt,
      );
      setNewName('');
      forgetRoot(state.rootAlbumId);
      await reload();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'errore');
    } finally {
      setCreating(false);
    }
  }

  function startRename(t: DecryptedTag) {
    setEditingId(t.tag.id);
    setEditName(t.name);
  }

  async function handleRename(e: FormEvent) {
    e.preventDefault();
    if (typeof state !== 'object' || 'error' in state) return;
    if (!editingId) return;
    const name = editName.trim();
    if (!name) return;
    setBusyId(editingId);
    try {
      const encryptedName = await cv.encryptText(name, state.albumKey);
      await metadata.renameTag(
        editingId,
        { rootAlbumId: state.rootAlbumId, encryptedName },
        session.jwt,
      );
      setEditingId(null);
      forgetRoot(state.rootAlbumId);
      await reload();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'errore');
    } finally {
      setBusyId(null);
    }
  }

  async function handleDelete(t: DecryptedTag) {
    if (typeof state !== 'object' || 'error' in state) return;
    if (
      !window.confirm(
        `Eliminare il tag "${t.name}"? Verrà rimosso da tutte le foto a cui è applicato.`,
      )
    ) {
      return;
    }
    setBusyId(t.tag.id);
    try {
      await metadata.deleteTag(t.tag.id, state.rootAlbumId, session.jwt);
      forgetRoot(state.rootAlbumId);
      await reload();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'errore');
      setBusyId(null);
    }
  }

  return (
    <div className="flex flex-col gap-7">
      <div className="flex flex-col gap-3">
        <PageEyebrow>tag</PageEyebrow>
        <Breadcrumb albumId={state.albumId} />
        <h2 className="font-display text-2xl font-semibold">
          Tag <span className="accent-text">della radice</span>
        </h2>
        <p className="text-sm text-on-surface-variant">
          I tag sono cifrati con la chiave dell'album radice. Sono trasversali ai sotto-album e
          condivisi (in sola lettura) con chi riceve la condivisione.
        </p>
      </div>

      <Card className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <Icon name="add_circle" className="text-2xl text-cyan" />
          <h3 className="font-display text-lg font-semibold">Nuovo tag</h3>
        </div>
        <form onSubmit={handleCreate} className="flex flex-col gap-3 md:flex-row md:items-end">
          <div className="flex-1">
            <Input
              label="nome"
              placeholder="es. Mare"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              {...(createError ? { error: createError } : {})}
              disabled={creating}
            />
          </div>
          <Button type="submit" loading={creating} leftIcon={<Icon name="add" />}>
            Crea
          </Button>
        </form>
      </Card>

      <div className="flex flex-col gap-3">
        <h3 className="font-display text-lg font-semibold">
          {sortedTags.length === 0
            ? 'Nessun tag'
            : sortedTags.length === 1
              ? '1 tag'
              : `${sortedTags.length} tag`}
        </h3>
        {sortedTags.length === 0 ? (
          <Card className="flex flex-col items-center gap-2 py-8 text-center">
            <Icon name="sell" className="text-3xl text-on-surface-variant" />
            <p className="text-sm text-on-surface-variant">
              Nessun tag in questa radice. Creane uno qui sopra.
            </p>
          </Card>
        ) : (
          <div className="flex flex-col gap-2">
            {sortedTags.map((dt) => {
              const isEditing = editingId === dt.tag.id;
              const isBusy = busyId === dt.tag.id;
              return (
                <Card key={dt.tag.id} className="flex items-center gap-3">
                  {isEditing ? (
                    <form onSubmit={handleRename} className="flex flex-1 items-center gap-2">
                      <div className="flex-1">
                        <Input
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          disabled={isBusy}
                          autoFocus
                        />
                      </div>
                      <Button
                        type="submit"
                        size="sm"
                        loading={isBusy}
                        leftIcon={<Icon name="save" />}
                      >
                        Salva
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingId(null)}
                        disabled={isBusy}
                      >
                        Annulla
                      </Button>
                    </form>
                  ) : (
                    <>
                      <Link
                        to={`/albums/${encodeURIComponent(state.albumId)}/tags/${encodeURIComponent(dt.tag.id)}`}
                        className="flex flex-1 items-center gap-3 text-left"
                      >
                        <Icon name="sell" className="text-xl text-cyan" />
                        <span className="font-display text-base font-semibold">{dt.name}</span>
                        <Icon
                          name="arrow_forward"
                          className="ml-auto text-base text-on-surface-variant"
                        />
                      </Link>
                      <button
                        type="button"
                        onClick={() => startRename(dt)}
                        disabled={isBusy}
                        aria-label={`Rinomina ${dt.name}`}
                        className="rounded-full p-2 text-on-surface-variant transition-colors hover:bg-surface-container-low hover:text-on-surface disabled:opacity-50"
                      >
                        <Icon name="edit" className="text-base" />
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleDelete(dt)}
                        disabled={isBusy}
                        aria-label={`Elimina ${dt.name}`}
                        className="rounded-full p-2 text-on-surface-variant transition-colors hover:bg-error/10 hover:text-error disabled:opacity-50"
                      >
                        <Icon name="delete" className="text-base" />
                      </button>
                    </>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function Breadcrumb({ albumId }: { albumId: string }) {
  return (
    <nav className="flex flex-wrap items-center gap-2 text-sm">
      <Link to="/albums" className="text-on-surface-variant transition-colors hover:text-cyan">
        Album
      </Link>
      <Icon name="chevron_right" className="text-base text-on-surface-variant" />
      <Link
        to={`/albums/${encodeURIComponent(albumId)}`}
        className="text-on-surface-variant transition-colors hover:text-cyan"
      >
        Dettaglio
      </Link>
      <Icon name="chevron_right" className="text-base text-on-surface-variant" />
      <span className="font-display text-base font-semibold text-cyan">Tag</span>
    </nav>
  );
}
